import { useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import type { TagCategory } from '@trinity/chat';
import { Modal, Header } from '../Modal';
import type { UseModalProps } from '../Modal';
import Button from '../presentation/Button';
import { ButtonActivityIndicator } from '../presentation/Button.ActivityIndicator';
import type { CategoryOption } from './TagForm';
import { TagForm } from './TagForm';
import { TagFormContainer } from './TagFormContainer';
import { TagFormContext } from './TagFormContext';

export type EditTagFormParams = {
  title: string;
  color: string;
  includeSummary: boolean;
  parentId: number | null;
  tagCategory: TagCategory | null;
};

type Props = UseModalProps & {
  tag: {
    parent?: {
      id: number;
    } | null;
    id: number;
    base: {
      color: string;
      name: string;
      id: number;
      tagCategory?: TagCategory | null;
    };
    ordinal: number;
    includeSummary: boolean;
  };
  hasChildren: boolean;
  allowedCategories?: CategoryOption[];
  onSave: (params: EditTagFormParams) => Promise<unknown>;
  isPending: boolean;
};

export const EditTag = ({ open, onClose, tag, hasChildren, allowedCategories, onSave, isPending }: Props) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Header>Edit Tag</Header>
      <TagFormContainer
        defaultState={{
          parentId: tag.parent?.id ?? null,
          title: tag.base.name,
          color: tag.base.color,
          category: tag.base.tagCategory ?? null,
          includeSummary: tag.includeSummary,
        }}
      >
        <ModalContent
          onClose={onClose}
          projectTagId={tag.id}
          hasChildren={hasChildren}
          allowedCategories={allowedCategories}
          onSave={onSave}
          isPending={isPending}
        />
      </TagFormContainer>
    </Modal>
  );
};

type ContentProps = {
  onClose?: () => unknown;
  projectTagId: number;
  hasChildren: boolean;
  allowedCategories?: CategoryOption[];
  onSave: (params: EditTagFormParams) => Promise<unknown>;
  isPending: boolean;
};

const ModalContent = ({
  onClose,
  projectTagId,
  hasChildren,
  allowedCategories,
  onSave,
  isPending,
}: ContentProps) => {
  const formCtx = useContext(TagFormContext);

  if (!formCtx) throw new Error('EditTag must be used within a TagFormContext provider');

  const { data } = formCtx;

  const canSave = useMemo(() => tagFormIsValid(data), [data]);

  const handleSave = useCallback(() => {
    onSave({
      title: data.title,
      color: data.color,
      includeSummary: data.includeSummary,
      parentId: data.parentId ?? null,
      tagCategory: data.category,
    }).then(onClose);
  }, [onSave, onClose, data]);

  return (
    <>
      <TagForm
        disableParent={hasChildren}
        excludeParentIds={[projectTagId]}
        allowedCategories={allowedCategories}
      />
      <Actions>
        <Button variant="brick" color="destructive" onClick={onClose}>
          Cancel
        </Button>
        <ButtonActivityIndicator
          disabled={!canSave}
          implicitDisable={false}
          loading={isPending}
          variant="brick"
          onClick={handleSave}
        >
          Save
        </ButtonActivityIndicator>
      </Actions>
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const tagFormIsValid = (form: { title: string }) => {
  return form.title?.length > 3 && form.title.length < 64 && form.title.replace(/\s/g, '').length > 3;
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const Actions = styled.div`
  display: flex;
  justify-content: space-evenly;
`;
